/**
 * WorkshopsCalendarChip — one workshop's chip inside a day cell.
 *
 * Week view (and sparse Month days) render the full multi-line chip; dense
 * Month days render a compact single-line row that expands to the full chip on
 * hover. The left accent is the venue's subregion color, or `--nb-primary` for
 * the persona's own ("mine") workshops. Owns its hover state; theme-aware via
 * `--nb-*` tokens.
 */
import type { ReactElement } from 'react';

import { useWorkshopChipHover } from './use-workshop-chip-hover';
import { chipColors } from './workshops-calendar-chip-shared';
import { fmtTime } from './workshops-calendar-dates';
import { WorkshopsCalendarChipCompact } from './WorkshopsCalendarChipCompact';
import { WorkshopsCalendarChipFull } from './WorkshopsCalendarChipFull';
import type { WorkshopChip, WorkshopClickHandler } from './workshopsCalendarTypes';

export interface WorkshopsCalendarChipProps {
  workshop: WorkshopChip;
  view: 'Month' | 'Week';
  /** Dense Month days collapse to the compact row until hovered. */
  compact: boolean;
  onWorkshopClick?: WorkshopClickHandler;
}

export function WorkshopsCalendarChip({
  workshop,
  view,
  compact,
  onWorkshopClick,
}: WorkshopsCalendarChipProps): ReactElement {
  const { hovered, onMouseEnter, onMouseLeave } = useWorkshopChipHover();
  // Venue-local wall clock (BON-386) — never the absolute `date` instant.
  const timeLabel = fmtTime(workshop.localDate ?? workshop.date);
  const colors = chipColors(workshop);
  const clickable = Boolean(onWorkshopClick);
  const onClick = onWorkshopClick
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        onWorkshopClick(workshop);
      }
    : undefined;
  const shared = {
    w: workshop,
    timeLabel,
    colors,
    clickable,
    onClick,
    onMouseEnter,
    onMouseLeave,
  };
  return compact && !hovered ? (
    <WorkshopsCalendarChipCompact {...shared} />
  ) : (
    <WorkshopsCalendarChipFull {...shared} view={view} hovered={hovered} />
  );
}
